import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { useActiveScope, useUpdateActiveScope, type ActiveScopeItem } from './activeScope';

/**
 * slice-3794 (ADR-0061): "Owners/Warehouses" — PB's two File-menu windows, `w_warehouse_select`
 * ("Warehouse Select") and `w_owner_select` ("Owner Select"), folded into one dialog under the
 * profile menu (Chris 2026-09-28). Each column is PB's tick-list (`use_global` Y/N) of the
 * user's granted warehouses / owners in priority order, id + company, ticked per the session's
 * current active set (everything, at login). A group with `single_warehouse` / `single_owner`
 * = 'Y' gets a one-tick column (radio buttons). OK sends both sets; the server answers PB's own
 * messages ("Please select an Owner.", "You can select only one Owner at a time.") as 422s,
 * shown here. Cancel changes nothing.
 */
export function OwnersWarehousesDialog({ onClose }: { onClose: () => void }) {
  const query = useActiveScope(true);
  const update = useUpdateActiveScope();
  const [whs, setWhs] = useState<string[] | null>(null);
  const [owners, setOwners] = useState<string[] | null>(null);

  // Seed the ticks once, from the server's current active set.
  const data = query.data;
  useEffect(() => {
    if (!data) return;
    const ticked = (xs: ActiveScopeItem[] | undefined) =>
      (xs ?? []).filter((x) => x.active).map((x) => x.id);
    setWhs((prev) => prev ?? ticked(data.warehouses));
    setOwners((prev) => prev ?? ticked(data.owners));
  }, [data]);

  const ready = data !== undefined && whs !== null && owners !== null;

  function handleOk() {
    if (whs === null || owners === null) return;
    update.mutate({ warehouses: whs, owners }, { onSuccess: () => onClose() });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Owners/Warehouses"
      subtitle="Lists show only the ticked warehouses and owners, for this session"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleOk} disabled={!ready || update.isPending}>
            {update.isPending ? 'Saving…' : 'OK'}
          </Button>
        </>
      }
    >
      {query.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {query.isError && (
        <p role="alert" className="text-sm text-red-700">
          {query.error instanceof Error ? query.error.message : 'Could not load your scope.'}
        </p>
      )}
      {data !== undefined && whs !== null && owners !== null && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TickList
            title="Warehouses"
            name="active-warehouse"
            items={data.warehouses ?? []}
            single={data.singleWarehouse}
            selected={whs}
            onChange={setWhs}
          />
          <TickList
            title="Owners"
            name="active-owner"
            items={data.owners ?? []}
            single={data.singleOwner}
            selected={owners}
            onChange={setOwners}
          />
        </div>
      )}
      {update.error instanceof Error && (
        <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {update.error.message}
        </p>
      )}
    </Modal>
  );
}

function TickList({
  title,
  name,
  items,
  single,
  selected,
  onChange,
}: {
  title: string;
  name: string;
  items: ActiveScopeItem[];
  single: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(id: string, checked: boolean) {
    if (single) {
      onChange([id]);
      return;
    }
    // Keep priority order, whatever order the ticks were clicked in.
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onChange(items.filter((i) => next.has(i.id)).map((i) => i.id));
  }

  return (
    <fieldset className="min-w-0 rounded border border-slate-200">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
        {title}
        {single && <span className="ml-1 font-normal normal-case text-slate-500">(one only)</span>}
      </legend>
      {items.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-500">None granted.</p>
      ) : (
        <ul className="max-h-72 overflow-auto py-1">
          {items.map((i) => (
            <li key={i.id}>
              <label className="flex cursor-pointer items-center gap-2 px-3 py-1 text-sm hover:bg-slate-50">
                <input
                  type={single ? 'radio' : 'checkbox'}
                  name={name}
                  aria-label={`${title} ${i.id}`}
                  checked={selected.includes(i.id)}
                  onChange={(e) => toggle(i.id, e.target.checked)}
                />
                <span className="w-14 shrink-0 font-mono">{i.id}</span>
                <span className="truncate text-slate-600">{i.name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
